import React, { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { X } from 'lucide-react';

const INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';

/**
 * PWA 설치 및 업데이트 관리 컴포넌트
 * - 앱 업데이트 감지 및 알림
 * - 설치 프롬프트 표시
 * - 오프라인 상태 표시
 */
const PWAInstaller: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // PWA 업데이트 관리
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
    },
    onRegisterError(error: any) {
    },
  });

  // 설치 프롬프트 이벤트 처리
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (sessionStorage.getItem(INSTALL_DISMISSED_KEY) !== '1') {
        setShowInstallButton(true);
      }
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setShowInstallButton(false);
    };

    // 온라인/오프라인 상태 감지
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // PWA 설치 실행
  const handleInstallClick = async () => {
    if (!installPrompt) return;

    const result = await installPrompt.prompt();
    
    setInstallPrompt(null);
    setShowInstallButton(false);
  };

  // 앱 업데이트 실행
  const handleUpdateClick = () => {
    updateServiceWorker(true);
  };

  // 오프라인 알림 닫기
  const closeOfflineReady = () => {
    setOfflineReady(false);
  };

  return (
    <div className="fixed right-4 z-40 bottom-[80px] md:bottom-4 space-y-2">
      {/* 앱 설치 버튼 */}
      {showInstallButton && (
        <div className="bg-blue-600 text-white p-3 rounded-lg shadow-lg flex items-center space-x-2 max-w-[calc(100vw-2rem)]">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">앱 설치</p>
            <p className="text-xs opacity-90 truncate">새 앨범·내 앨범·갤러리 바로가기</p>
          </div>
          <button
            onClick={handleInstallClick}
            className="bg-white text-blue-600 px-3 py-1 rounded font-medium text-sm hover:bg-gray-100 transition-colors shrink-0"
          >
            설치
          </button>
          <button
            onClick={() => {
              sessionStorage.setItem(INSTALL_DISMISSED_KEY, '1');
              setShowInstallButton(false);
            }}
            aria-label="앱 설치 배너 닫기"
            className="p-1 rounded hover:bg-blue-700 transition-colors shrink-0"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* 업데이트 알림 */}
      {needRefresh && (
        <div className="bg-green-600 text-white p-3 rounded-lg shadow-lg flex items-center space-x-3">
          <div className="flex-1">
            <p className="font-semibold">새 버전 사용 가능</p>
            <p className="text-sm opacity-90">앱을 업데이트해 주세요</p>
          </div>
          <button
            onClick={handleUpdateClick}
            className="bg-white text-green-600 px-3 py-1 rounded font-medium hover:bg-gray-100 transition-colors"
          >
            업데이트
          </button>
        </div>
      )}

      {/* 오프라인 준비 완료 알림 */}
      {offlineReady && (
        <div className="bg-gray-800 text-white p-3 rounded-lg shadow-lg flex items-center space-x-3">
          <div className="flex-1">
            <p className="font-semibold">오프라인 준비 완료</p>
            <p className="text-sm opacity-90">인터넷 없이도 사용할 수 있습니다</p>
          </div>
          <button
            onClick={closeOfflineReady}
            className="bg-white text-gray-800 px-3 py-1 rounded font-medium hover:bg-gray-100 transition-colors"
          >
            확인
          </button>
        </div>
      )}

      {/* 오프라인 상태 표시 */}
      {isOffline && (
        <div className="bg-orange-600 text-white p-3 rounded-lg shadow-lg flex items-center space-x-3">
          <div className="flex-1">
            <p className="font-semibold">오프라인 모드</p>
            <p className="text-sm opacity-90">인터넷 연결을 확인해 주세요</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PWAInstaller; 